import { useState } from 'react';
import { toast } from 'sonner';
import { Lock, MapPin, Plus, Trash2, UserCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { VoiceSelect } from './VoiceLibrary';
import { absUrl, addKitAsset, errMsg, patchKitAsset, removeKitAsset } from '../../lib/aiStudioApi';

function KitCard({ project, asset, voices, onChanged }) {
  const ref = absUrl(asset.reference_urls?.[0]);
  const Icon = asset.kind === 'character' ? UserCircle2 : MapPin;
  const setVoice = async (voice_id) => {
    try { await patchKitAsset(project.id, asset.id, { voice_id }); toast.success(`${asset.name}'s voice set`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-3 rounded-xl border border-neutral-800 bg-neutral-950/60 p-3" data-testid={`kit-card-${asset.id}`}>
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        {ref ? <img src={ref} alt={asset.name} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-neutral-600"><Icon size={20} /><span className="px-1 text-center text-[9px]">ref on Produce</span></div>}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-neutral-100"><Lock size={11} className="text-amber-300" /> {asset.name}</div>
          <button type="button" onClick={() => removeKitAsset(project.id, asset.id).then(onChanged)} className="text-neutral-600 hover:text-red-300" data-testid={`kit-remove-${asset.id}`}><Trash2 size={13} /></button>
        </div>
        <p className="line-clamp-2 text-[11px] text-neutral-400">{asset.appearance || asset.description}</p>
        {asset.wardrobe && <p className="line-clamp-1 text-[11px] text-neutral-500">Wardrobe: {asset.wardrobe}</p>}
        {asset.kind === 'character' && (
          <div className="pt-1"><VoiceSelect voices={voices} value={asset.voice_id} onChange={setVoice} testId={`kit-voice-${asset.id}`} placeholder="Cast a voice…" /></div>
        )}
      </div>
    </div>
  );
}

export function ShowKitPanel({ project, voices, onChanged }) {
  const [form, setForm] = useState({ kind: 'character', name: '', appearance: '', wardrobe: '', urls: '' });
  const kit = project.show_kit || [];
  async function add() {
    if (!form.name.trim()) { toast.error('Name the character or location'); return; }
    try {
      await addKitAsset(project.id, {
        kind: form.kind, name: form.name.trim(), appearance: form.appearance.trim(), description: form.appearance.trim(),
        wardrobe: form.wardrobe.trim(), reference_urls: form.urls.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean),
      });
      setForm({ ...form, name: '', appearance: '', wardrobe: '', urls: '' });
      toast.success('Locked into the Show Kit'); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  }
  return (
    <div className="space-y-5" data-testid="show-kit-panel">
      {[['character', 'Cast'], ['location', 'Locations']].map(([kind, title]) => {
        const items = kit.filter((a) => a.kind === kind);
        return (
          <div key={kind}>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">{title} ({items.length}/8) · identity-locked across every shot</div>
            {items.length ? <div className="grid gap-3 md:grid-cols-2">{items.map((a) => <KitCard key={a.id} project={project} asset={a} voices={voices} onChanged={onChanged} />)}</div>
              : <div className="text-xs text-neutral-500">None yet — the AI Director creates these automatically, or add them below.</div>}
          </div>
        );
      })}
      <div className="rounded-xl border border-dashed border-neutral-800 p-3">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-neutral-500">Add to Show Kit</div>
        <div className="grid gap-2 md:grid-cols-[130px_1fr_2fr]">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="kit-kind-select"
            className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs"><option value="character">Character</option><option value="location">Location</option></select>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border-neutral-800 bg-neutral-950 text-sm" data-testid="kit-name-input" />
          <Input value={form.appearance} onChange={(e) => setForm({ ...form, appearance: e.target.value })} placeholder={form.kind === 'character' ? 'Look: face, hair, build, age' : 'Visual description'} className="border-neutral-800 bg-neutral-950 text-sm" data-testid="kit-appearance-input" />
          {form.kind === 'character' && <Input value={form.wardrobe} onChange={(e) => setForm({ ...form, wardrobe: e.target.value })} placeholder="Wardrobe" className="border-neutral-800 bg-neutral-950 text-sm md:col-start-2" data-testid="kit-wardrobe-input" />}
          <Input value={form.urls} onChange={(e) => setForm({ ...form, urls: e.target.value })} placeholder="Reference image URL(s) — optional" className="border-neutral-800 bg-neutral-950 text-sm md:col-start-3" data-testid="kit-urls-input" />
        </div>
        <div className="mt-2 flex justify-end"><Button size="sm" onClick={add} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="kit-add-btn"><Plus size={13} className="mr-1" /> Lock asset</Button></div>
      </div>
    </div>
  );
}
